import { useGraphQLQuery } from '@betterment/graphql';
import { useAutoNavigate } from '@betterment/js-runtime';
import { Box, Button, Card, H2, H3, Shimmer, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';

import {
  CommonReportsCreationOptionsDocument,
  CommonReportsCreationOptionsQuery,
} from './graphql/query.reportCreationOptions.generated';

import { ErrorBoundary } from '../../../components/error-boundary/ErrorBoundary';
import { SambaAutoLink } from '../../../components/SambaAutoLink';
import { urlBuilder } from '../../../utils/urlBuilder';

const reportBoxCss = css.raw({
  height: '100%',
  width: '100%',
  borderStyle: 'solid',
  borderWidth: 'thin',
  borderColor: 'border.weak',
  borderRadius: 'medium',
  cursor: 'pointer',
  padding: 'space.200',
  color: 'currentcolor',
  transition: 'background-color 300ms ease-out',
  _hover: {
    color: 'currentcolor',
    bg: 'background.surface.hover',
    textDecoration: 'none',
  },
});

const shimmerCss = {
  alignItems: 'center',
  height: 'size.200',
  width: '100%',
};

const ShimmerLoader = () => {
  return (
    <>
      {[...Array(3).keys()].map(key => (
        <Box key={key} css={reportBoxCss}>
          <Shimmer css={shimmerCss} />
          <Shimmer
            css={{
              ...shimmerCss,
              marginTop: 'space.100',
            }}
          />
        </Box>
      ))}
    </>
  );
};

const CommonReportsContent = () => {
  const employerId = useCurrentEmployerId();
  const { data: rawData } = useGraphQLQuery(CommonReportsCreationOptionsDocument, {
    variables: { employerId },
    throwOnError: true,
  });
  const data = rawData as CommonReportsCreationOptionsQuery | undefined;
  const commonReportOptions = data?.employer.reportCreationOptions.filter(report => report.isCommon);

  return (
    <Box
      css={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'space.150',
      }}
    >
      {!data && <ShimmerLoader />}
      {commonReportOptions?.map(report => (
        <SambaAutoLink key={report.id} css={reportBoxCss} to={urlBuilder.reportsNew({ report: report.altIdentifier })}>
          <H3 variant="title100">{report.name}</H3>
          <Text variant="text50" color="text.weak">
            {report.description}
          </Text>
        </SambaAutoLink>
      ))}
    </Box>
  );
};

const CommonReports = () => {
  const navigate = useAutoNavigate();
  return (
    <Card css={{ p: 'space.300', display: 'flex', flexDirection: 'column' }}>
      <ErrorBoundary name="commonReports">
        <Card.Header
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <H2 css={{ mb: 'space.300' }} variant="title200">
            Common reports
          </H2>
          <Button onClick={() => void navigate(urlBuilder.reportsNew({}))} css={{ mb: 'space.150' }}>
            Run report
          </Button>
        </Card.Header>
        <CommonReportsContent />
      </ErrorBoundary>
    </Card>
  );
};

export default CommonReports;
